import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";

// Growth-bucket ranges sourced from the BandwidthProblemSlide categories
// (Administrative & Operational Staff 10–14%, Revenue Leakage 10–25%,
// Missed Opportunity 28–55%) — surfaced here as the practical outcomes
// of crossing the inflection point.
const conditions = [
  {
    text: "If your staff called in sick for the rest of the week, you would not feel stressed.",
  },
  {
    text: "If you added 200 patients next month, you would not need to hire any additional admin or ops staff to deal with them — growth feels effortless.",
  },
  {
    text: "Your profit margins are tight — you're not losing money to your revenue cycle, scheduling gaps, missed calls, or any of it.",
  },
  {
    text: "We have helped you unlock growth — if you want to do any of the following, it feels easy:",
    sub: [
      "Reduce cost by 10–12% through staff time savings",
      "Fix revenue leakage by 10–30%",
      "Grow your office by 30–50%",
    ],
  },
];

const AutomationInflectionPointSlide = () => {
  return (
    <div className="h-full w-full bg-slate-50 flex flex-col p-6 xl:p-10 relative overflow-hidden font-sans">
      {/* Background blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-blue-100/40 rounded-full blur-[120px] -translate-y-1/3 translate-x-1/4" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-indigo-100/40 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4" />
      <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:32px_32px] opacity-40" />

      <div className="max-w-[1100px] w-full mx-auto flex flex-col h-full relative z-10 justify-center">
        {/* Title */}
        <div className="text-center mb-8 flex-shrink-0">
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl lg:text-5xl font-extrabold text-slate-900 tracking-tight leading-[1.08] mb-4"
          >
            Our goal is to use AI to run as much of your practice{" "}
            <span className="text-blue-600">on autopilot</span> as possible.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.45 }}
            className="text-[18px] lg:text-[22px] text-slate-700 font-medium max-w-[1000px] mx-auto leading-[1.5]"
          >
            We call this the{" "}
            <span className="font-semibold text-slate-900">
              &ldquo;automation inflection point&rdquo;
            </span>{" "}
            — the point where you've automated enough of your practice that you
            can check off every one of the following:
          </motion.p>
        </div>

        {/* Checklist card */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-white rounded-2xl border border-slate-200 shadow-[0_20px_60px_-20px_rgba(15,23,42,0.1)] p-7 lg:p-8"
        >
          <ul className="space-y-5">
            {conditions.map((c, i) => (
              <li key={i} className="flex items-start gap-3.5">
                <div className="w-7 h-7 rounded-md bg-blue-50 border border-blue-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check
                    className="w-4 h-4 text-blue-600"
                    strokeWidth={2.6}
                  />
                </div>
                <div className="flex-1">
                  <p className="text-[17px] lg:text-[18px] text-slate-800 leading-[1.45] font-medium">
                    {c.text}
                  </p>
                  {c.sub && (
                    <ul className="mt-2.5 ml-1 space-y-1.5">
                      {c.sub.map((s, j) => (
                        <li
                          key={j}
                          className="flex items-start gap-2.5 text-[15px] text-slate-600 leading-snug"
                        >
                          <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0 mt-[9px]" />
                          <span>{s}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </div>
  );
};

export default AutomationInflectionPointSlide;
